import React, { useState } from 'react';
import useLocalOpsAlertTest from './useLocalOpsAlertTest';

const reasons = {
  disabled: 'Test alerts are temporarily unavailable. Try again later.',
  opted_out:
    'Alerts are off by choice. Use Enable alerts if you want to turn them on.',
  type_muted:
    'System alerts are muted in your notification preferences. This test respects that choice.',
  quiet_hours:
    'Your quiet hours paused this test. Send a new test after quiet hours.',
  registration_changed:
    'This registration changed. Use the existing Reconnect alerts control before another test.',
  rate_limited:
    'You can start three tests per minute. Wait a minute before a new test; checking status is still available.',
  provider_failed:
    'The push service did not accept this test. Check this device’s notification settings before another test.',
};
export default function LocalOpsAlertTest({ jwt, status }) {
  const enabled = status.subscribed && status.testAvailable && !status.busy;
  const test = useLocalOpsAlertTest({ jwt, enabled });
  const [help, setHelp] = useState(false);
  const attempt = test.attempt;
  const historical = attempt?.currentBinding === false;
  const unknown = attempt?.outcome === 'unknown';
  const receipt = attempt?.playerReportedReceivedAt;
  const message = test.busy
    ? test.action === 'send'
      ? 'Sending your test alert…'
      : test.action === 'confirm'
      ? 'Saving your confirmation…'
      : 'Checking this test…'
    : historical
    ? 'This test belongs to an earlier registration. Its result is kept as history.'
    : receipt
    ? 'You confirmed receiving a test on this device.'
    : attempt?.outcome === 'accepted'
    ? 'Test sent. Check this device’s notifications.'
    : unknown
    ? 'Outcome unknown. The earlier test may have been sent. Check status before starting another test.'
    : reasons[attempt?.reason] || '';
  if (!status.testAvailable && !attempt) return null;
  return (
    <section className="jgAlertTest" aria-label="LocalOps test alert">
      <div className="jgAlertsSheetEyebrow">LocalOps alerts</div>
      <p className="jgAlertTestHint">
        This tests the current browser or Home Screen app, not every device on
        your account.
      </p>
      {message && (
        <div role="status" aria-live="polite" className="jgAlertTestStatus">
          {message}
        </div>
      )}
      {receipt && (
        <time dateTime={receipt}>{new Date(receipt).toLocaleString()}</time>
      )}
      {test.error && (
        <div role="alert" className="jgAlertTestError">
          {test.error}
        </div>
      )}
      <button
        type="button"
        className="jgAlertsPrimary"
        disabled={!enabled || test.busy}
        onClick={test.send}
      >
        {unknown
          ? 'Send a new test (earlier test may have sent)'
          : 'Send a test alert to this device'}
      </button>
      {attempt && (
        <button
          type="button"
          className="jgPushManage"
          disabled={!enabled || test.busy}
          onClick={test.check}
        >
          Check test status
        </button>
      )}
      {attempt?.attemptId &&
        !historical &&
        ['accepted', 'unknown'].includes(attempt.outcome) && (
          <button
            type="button"
            className="jgPushManage"
            aria-disabled={!enabled || test.busy || !!receipt}
            onClick={
              enabled && !test.busy && !receipt ? test.confirm : undefined
            }
          >
            {receipt ? 'Receipt recorded' : 'I received it'}
          </button>
        )}
      <button
        type="button"
        className="jgPushManage"
        aria-expanded={help}
        onClick={() => setHelp((value) => !value)}
      >
        Help me troubleshoot
      </button>
      {help && (
        <p className="jgAlertTestHint">
          Check notifications for this browser or Home Screen app in your device
          settings, including Focus or Do Not Disturb. Return here and check the
          same test’s status. A test without confirmation does not mean alerts
          are broken.
        </p>
      )}
    </section>
  );
}
